// Start-up: the language first, because nothing can be drawn without its text; the page next; then the languages on
// offer and, signed in, who this is and their push registration - behind the page. The Blazor MainLayout's
// OnInitializedAsync, in an order that puts the page on screen first.

import { getAccessToken, initiateLogin, isAuthenticated, setAccountLanguage } from './auth.ts';
import { cabinetApi } from './cabinet-api.ts';
import { ensureTranslations, hasTranslations } from './i18n.ts';
import { initLayout, showLayout } from './layout.ts';
import { initLegalGate } from './legal.ts';
import { cachedLocales, fetchLocales, pickLocale, rememberLanguage } from './localization.ts';
import { log } from './logs.ts';
import { requestPermissionAndRegister } from './push.ts';
import { navigate, route, startRouter } from './router.ts';
import { logout } from './session.ts';
import { emit, on, state } from './state.ts';

import { dashboard } from './pages/dashboard.ts';
import { home } from './pages/home.ts';
import { notFound, signin, stripeReturn } from './pages/other.ts';
import { withdrawHistory } from './pages/withdraw-history.ts';

/** Shows the cabinet in another language: remembered, loaded, every page drawn again. */
async function switchLanguage(name: string): Promise<void> {
    if (!name) return;

    rememberLanguage(name);
    state.locale = name;
    await ensureTranslations(name);
    emit('languageChanged');
}

/** The header's picker: the account follows, and so does the language pushes are written in. */
async function chooseLanguage(name: string): Promise<void> {
    await switchLanguage(name);

    if (isAuthenticated()) {
        await setAccountLanguage(name);
        await requestPermissionAndRegister();
    }
}

const landingActions = { onLanguage: (name: string) => switchLanguage(name).catch((error: unknown) => log.error('Language failed', error)) };

route('/', ctx => home(ctx, landingActions), { layout: 'none' });
route('/dashboard', dashboard, { layout: 'cabinet', auth: true });
route('/withdraw-history', withdrawHistory, { layout: 'cabinet', auth: true });
route('/stripe/return', stripeReturn, { layout: 'cabinet' });
route('/stripe/refresh', stripeReturn, { layout: 'cabinet' });
route('/identity-signin', ctx => signin(ctx, { login: initiateLogin, onLanguage: switchLanguage }), { layout: 'none' });
route('/not-found', notFound, { layout: 'cabinet' });
route('*', notFound, { layout: 'cabinet' });

let personLoaded = false;

/** Who is signed in, and their account's language, which wins over the one chosen here. */
async function loadPerson() {
    if (!isAuthenticated()) return;

    const response = await cabinetApi('personal-info');

    if (!response.ok) {
        log.error(`Error retrieving personal info: ${response.status}`, undefined, undefined, 'Lify.Influencer.Components.Layout.MainLayout');

        return;
    }

    state.person = response.json();
    personLoaded = true;
    emit('personChanged');

    const language = await cabinetApi('lify/account/language');
    const stored = language.ok ? language.string().trim() : '';

    if (stored && stored !== state.locale) await switchLanguage(stored);
}

/** On every signed-in load: the person, then push - asked once per origin by the browser, refreshed every time. */
async function onSignedIn() {
    if (!isAuthenticated()) {
        state.person = null;
        personLoaded = false;

        return;
    }

    if (!personLoaded) await loadPerson();

    await requestPermissionAndRegister();
}

async function boot() {
    initLayout({
        onLanguage: name => chooseLanguage(name).catch(error => log.error('Language failed', error)),
        onLogout: () => logout()
    });

    initLegalGate({ onSignOut: () => logout() });

    // A notification clicked while a cabinet tab is open: the worker focuses it and says so.
    navigator.serviceWorker?.addEventListener('message', event => {
        if (event.data?.type === 'PUSH_NOTIFICATION_CLICK' && isAuthenticated()) navigate('/dashboard');
    });

    let locales = cachedLocales();
    let locale = pickLocale(locales.map(l => l.name));
    let localesFresh = false;

    state.locales = locales;
    state.locale = locale;

    if (!hasTranslations(locale)) {
        // A first visit: the languages and the text together, since both wait on the same WAF token.
        const [fresh] = await Promise.all([fetchLocales(), ensureTranslations(locale)]);

        if (fresh) {
            locales = fresh;
            localesFresh = true;
            state.locales = fresh;

            const chosen = fresh.find(l => l.name === locale) || fresh.find(l => l.isDefault);

            if (chosen && chosen.name !== locale) {
                locale = chosen.name;
                state.locale = locale;
                await ensureTranslations(locale);
            }
        }
    } else {
        await ensureTranslations(locale);
    }

    startRouter({
        show: layout => showLayout(layout),
        isAuthenticated,
        login: () => initiateLogin().catch(error => log.error('Login failed', error))
    });

    // English underneath every other language, for a key not translated yet.
    if (locale !== 'en') ensureTranslations('en').then(() => emit('languageChanged')).catch(() => {});

    if (!localesFresh) {
        const fresh = await fetchLocales();

        if (fresh) {
            state.locales = fresh;

            if (!fresh.some(l => l.name === state.locale)) {
                const fallback = fresh.find(l => l.isDefault);

                if (fallback) {
                    state.locale = fallback.name;
                    await ensureTranslations(fallback.name);
                    emit('languageChanged');
                }
            }

            emit('localesChanged');
        }
    }

    on('authChanged', () => onSignedIn().catch(error => log.error('Signed-in start-up failed', error)));

    if (getAccessToken()) await onSignedIn();
}

boot().catch(error => log.error('Start-up failed', error, undefined, 'Lify.Influencer.Components.Layout.MainLayout'));
